import { forwardRef, useImperativeHandle, useMemo } from 'react'
import { FormHelperText, Typography } from '@mui/material'
import { Box, Stack } from '@mui/system'
import { useDropzone } from 'react-dropzone'
import { Controller, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

export const MAX_UPLOAD_SIZE = 2000000

const ImagePicker = forwardRef(
  ({ name, picture, onPictureChange }, ref) => {
    const { t } = useTranslation()
    const {control} = useFormContext()
    const handleDrop = (acceptedFiles) => {
      const picture = acceptedFiles?.[0] ?? null

      if (!picture) {
        onPictureChange?.(null, null)
        return
      }

      if (picture) {
        //Convert image to b64 data
        const reader = new FileReader()
        reader.readAsDataURL(picture)
        reader.onload = () => {
          onPictureChange?.(reader.result?.toString() ?? null, picture)
        }
      }
    }

    const {
      getRootProps,
      getInputProps,
      isDragReject,
      fileRejections,
      rootRef,
      open,
    } = useDropzone({
      onDrop: handleDrop,
      multiple: false,
      accept: {
        'image/png': ['.png'],
        'image/jpeg': ['.jpeg', '.jpg'],
      },
      minSize: 0,
      maxSize: MAX_UPLOAD_SIZE, // Limit to 2MB
    })

    // append function open to forwarded ref to allow programtic call
    useImperativeHandle(
      ref,
      () => ({
        open() {
          open()
        },
      }),
      [rootRef],
    )

    const isFileTooLarge = useMemo(
      () =>
        fileRejections.length > 0 &&
        fileRejections[0].file.size > MAX_UPLOAD_SIZE,
      [fileRejections],
    )

    return (
      <Controller
        name={name}
        control={control}
        defaultValue={picture}
        render={() => (
          <Box
            {...getRootProps()}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              height: '100%',
              border: 'dotted 2px #DDD',
              borderRadius: '8px',
            }}
          >
            <input {...getInputProps()} />
            <Stack
              gap={1}
              alignItems="center"
              justifyContent="center"
              color="#DADDE3" //TODO: Use theme ?
            >
              <Box>
                <i
                  style={{ fontSize: '32px' }}
                  className="fa-solid fa-cloud-arrow-up"
                ></i>
              </Box>
              <Typography
                sx={{
                  fontSize: '14px',
                  lineHeight: '21px',
                  color: 'content.secondary',
                  fontWeight: '500',
                }}
              >
                {t('advertisements.edition.content.picture.main_drop_text')}
              </Typography>
              <Typography
                sx={{
                  fontSize: '12px',
                  lineHeight: '18px',
                  color: 'content.tertiary',
                  fontWeight: '400',
                  textAlign: 'center',
                }}
              >
                <p>
                  {t(
                    'advertisements.edition.content.picture.secondary_drop_text',
                  )}
                </p>
                <p>
                  {t(
                    'advertisements.edition.content.picture.tertiary_drop_text',
                  )}
                </p>
                <Stack alignItems="center">
                  {isFileTooLarge && (
                    <FormHelperText error>
                      {t('advertisements.edition.errors.file_too_large')}
                    </FormHelperText>
                  )}
                  {isDragReject && (
                    <FormHelperText error>
                      {t('advertisements.edition.errors.drop_error')}
                    </FormHelperText>
                  )}
                </Stack>
              </Typography>
            </Stack>
          </Box>
        )}
      />
    )
  },
)

export default ImagePicker
